import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Fade,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { H2 } from "../../../../components/text/headings";
import { useEffect, useRef, useState } from "react";
import {
  useGetSupplierOverviewData,
  useGetTotalEmissions,
} from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import LocalSpinner from "../../../../components/LocalSpinner";
import { CaretDown, ChartDonut, Check, ListBullets, TrendDown, TrendUp } from "@phosphor-icons/react";
import { useGetAllSupplierCategories, useGetOrganizationStructure } from "../../../../hooks/query";
import { useSearchParams } from "react-router-dom";
import { ScopeCard } from "../components/SummaryCards";
import DashboardCard from "../../../ISSB/Dashboard/components/DashboardCard";
import { getFormattedNumValue, showErrorToast } from "../../../../utils";
import ActiveSupplierCard from "./ActiveSupplierCard";
import AttributedEmissions from "./AttributedEmissions";
import Scope3Cat1 from "./Scope3Cat1";
import SupplierInsightsVisual from "./SupplierInsightsVisual";
import * as React from "react";
import SupplierFiltersCatQualityYear from "./SupplierFiltersCatQualityYear";
import assert from "utils/assert";
import DataQualityCard from "./DataQualityCard";
import SupplierTrends from "./SupplierTrends";

/**
 * Top half of the SupplierInsights page
 */
const SupplierCards = () => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentYearData, setCurrentYearData] = useState(null);
  const [previousYearData, setPreviousYearData] = useState(null);

  const detailView = searchParams.get("detailView");

  const [filters, setFilters] = useState();

  // States
  const [selectedViewType, setSelectedViewType] = useState("summary");

  const handleEmissionsError = (error, year) => {
    if (error?.response?.status === 404) {
      toast({
        title: "Report not available for year " + year,
        description: "The emissions report for this year is not available.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    } else {
      toast({
        title: "Error fetching current year emissions data",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // calls .get(`/insights/suppliers?categories=${categories}&years=${years}&dataQuality=${dataQuality}`, config)
  const { data: overviewData, isFetching } = useGetSupplierOverviewData(
    [filters?.year, filters?.year - 1],
    filters?.categories,
    filters?.dataQuality,
    null,
    {
      onError: (error) => handleEmissionsError(error, filters?.year),
      cacheTime: 60, // 1 minute
    }
  );
  useEffect(() => {
    if (overviewData) {
      setCurrentYearData(overviewData[filters?.year]);
      setPreviousYearData(overviewData[filters?.year - 1]);
    }
  }, [overviewData, filters?.year]);

  return (
    <Flex direction="column" gap={4}>
      <SupplierFiltersCatQualityYear
        filters={filters}
        setFilters={setFilters}
        showFilters={["year", "quality", "category"]}
      />

      {!detailView && (
        <>
          <Flex width="fit-content" alignSelf="flex-end" mt={2}>
            <ToggleButton
              options={[
                { label: "Summary", value: "summary", icon: <ListBullets size={18} /> },
                { label: "Visual", value: "visual", icon: <ChartDonut size={18} /> },
              ]}
              selected={selectedViewType}
              setSelected={setSelectedViewType}
            />
          </Flex>
          <H2 fontSize="body.2xl">Supplier Attributed Emissions</H2>
          <Fade in={selectedViewType === "summary"} unmountOnExit>
            <SummaryCards
              isFetching={isFetching}
              currentYearData={currentYearData}
              previousYearData={previousYearData}
              isFetchingCurrentYear={isFetching}
              isFetchingPreviousYear={isFetching}
            />
          </Fade>
          <Fade in={selectedViewType === "visual"} unmountOnExit>
            <SupplierInsightsVisual
              filters={filters}
              setFilters={setFilters}
              currentYearData={currentYearData}
              previousYearData={previousYearData}
            />
          </Fade>
        </>
      )}

      <SupplierTrends setFilters={setFilters} filters={filters} />
    </Flex>
  );
};

export const ToggleButton = ({
  options,
  selected,
  setSelected,
  colorOn = "white",
  colorOff = "gray.100",
  colorHover = "white",
}) => {
  return (
    <ButtonGroup isAttached width="100%">
      {options.map((option) => (
        <Tooltip label={`Change view type to ${option.value}`} key={option.value}>
          <Button
            px={8}
            minW="100px"
            onClick={() => setSelected(option.value)}
            bg={selected === option.value ? colorOn : colorOff}
            _hover={{ bg: selected === option.value ? colorOn : colorHover }}
            flex="1"
            rightIcon={<Box fontWeight={selected === option.value ? "bold" : "normal"}>{option.icon}</Box>}
            fontWeight={selected === option.value ? "bold" : "normal"}
            border="solid 1px"
            borderRadius="lg"
            borderColor={selected === option.value ? "gray.300" : "gray.200"}
            boxShadow={selected === option.value ? "sm" : "none"}
          >
            {option.label || option.value}
          </Button>
        </Tooltip>
      ))}
    </ButtonGroup>
  );
};

export const getCategoryParams = (categories) => {
  const params = new URLSearchParams();

  categories?.forEach((name) => {
    if (name) {
      params.append("categories", name);
    }
  });

  return params.toString();
};

const SummaryCards = ({ isFetching, currentYearData, previousYearData }) => {
  return (
    <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={6} align="stretch">
      <GridItem h="100%" minH="350px">
        <ActiveSupplierCard isLoading={isFetching} currentYear={currentYearData} previousYear={previousYearData} />
      </GridItem>
      <GridItem h="100%" minH="350px">
        <AttributedEmissions isLoading={isFetching} currentYear={currentYearData} previousYear={previousYearData} />
      </GridItem>
      {/*<GridItem h="100%" minH="400px">*/}
      {/*  <Scope3Cat1 isLoading={isFetching} currentYear={currentYearData} previousYear={previousYearData} />*/}
      {/*</GridItem>*/}
      <GridItem h="100%" minH="350px">
        <DataQualityCard isLoading={isFetching} currentYear={currentYearData} previousYear={previousYearData} />
      </GridItem>
    </Grid>
  );
};

/**
 * TODO: only use via SupplierFiltersCatQualityYear
 * @param {*} param0
 * @returns
 */
export const CategoryDropdown = ({ selected, setSelected }) => {
  assert(!selected || Array.isArray(selected), selected);
  const toast = useToast();

  const selectedCount = selected.length || 0;

  const { data: categories, isLoading } = useGetAllSupplierCategories({
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const toggleItem = (key, name) => {
    setSelected((prev) => {
      const exists = prev.includes(name);
      return exists ? prev.filter((itemName) => itemName !== name) : [...prev, name];
    });
  };

  const handleClearAll = () => {
    setSelected([]);
  };

  return (
    <Menu closeOnSelect={false}>
      {isLoading ? (
        <LocalSpinner />
      ) : (
        <MenuButton as={Button} variant="outline" rightIcon={<CaretDown size={16} />}>
          {/*{selectedCount > 0 ? `Categories (${selectedCount} selected)` : "Categories"}*/}
          {
            selectedCount === 0
              ? "Categories"
              : selectedCount === 1
                ? selected && selected[0]
                : `Categories (${selectedCount} selected)`
          }

        </MenuButton>
      )}

      <MenuList maxHeight="400px" overflowY="auto">
        {selectedCount > 0 && (
          <MenuItem color="status.red" fontSize="body.xxs" px={3} py={2} onClick={handleClearAll}>
            Clear All
          </MenuItem>
        )}
        {Object.entries(categories || {}).map(([key, item]) => {
          return (
            <MenuItem key={key} onClick={() => toggleItem(key, item.name)}>
              <HStack justifyContent="space-between" w="full">
                <Text fontWeight={selected?.includes(item.name) ? "bold" : "normal"}>{item.name}</Text>
                {selected?.includes(item.name) ? <Check size={16} weight="bold" color="#82D909" /> : null}
              </HStack>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};

export const DataQualityDropdown = ({ selected, setSelected }) => {
  const options = ["ALL", "VERIFIED", "UNVERIFIED", "MIXED"];

  return (
    <Menu>
      <MenuButton as={Button} variant="outline" rightIcon={<CaretDown size={16} />}>
        {selected || "All Data Quality"}
      </MenuButton>
      <MenuList>
        {options.map((option) => (
          <MenuItem
            key={option}
            onClick={() => setSelected(option)}
            fontWeight={selected === option ? "bold" : "normal"}
          >
            {option}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

export default SupplierCards;
